import { z } from "zod";
// The team, coverage and customer support contract of the admin (CHT-768, Phase 13.2). Routes in
// `apps/api/src/routes/staff-team-routes.ts`; the admin calls them only through `@lafayette/api-client` (`staff-team.ts`).
//
// STAFF ONLY and closed, like `staff-attention.ts`: strict shapes (an unlisted field fails the parse, 500), closed codes repeated from the
// domain and application (parity-tested in `apps/api/src/routes/staff-team-routes.test.ts`), ISO 8601 text for times. Reads hold ids,
// display names, codes, counts and times: no email, no phone, no auth id, no message text. Every command carries a `reason` (a staff
// reason, kept in the audit row only), an `Idempotency-Key` header, and the "what the screen saw" check (`expectedVersion` of a member,
// `expectedActiveAssignmentIds` of a customer, `expectedOpenWindowIds` of a person's unavailability): a stale screen is a 409 and nothing
// is written. The directory and the gaps are paged with an opaque cursor (CHT-768 review fix 6).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
/** A staff-internal reason. Never a customer view, a card, a notice, or analytics. */
const staffReason = z.string().trim().min(1).max(500);
const displayName = z.string().min(1).max(200);
/** The version token of a member (`staffVersionToken`: a SHA-256 in hex). Opaque to the screen; it only sends it back. */
const versionToken = z.string().regex(/^[0-9a-f]{64}$/u);
/** An opaque page cursor. The server decides what it holds and refuses one that is not its own. */
const pageCursor = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9_-]+$/u);
export const staffTeamRoleValues = [
    "concierge",
    "supervisor",
    "catalog_editor",
    "administrator",
];
export const staffTeamStatusValues = ["ACTIVE", "DEACTIVATED"];
export const staffAssignmentTypeValues = ["PRIMARY", "BACKUP", "TEMPORARY"];
export const staffAvailabilityReasonValues = [
    "ON_LEAVE",
    "OFF_SHIFT",
    "SICK",
    "TRAINING",
    "OTHER",
];
export const staffCoverageGapKindValues = [
    "UNASSIGNED_HANDOFF",
    "CUSTOMER_WITHOUT_PRIMARY",
    "PRIMARY_UNAVAILABLE",
];
const staffRef = z.strictObject({ staffUserId: uuid, displayName });
const customerRef = z.strictObject({ id: uuid, displayName });
// --- reads -----------------------------------------------------------------------------------------------------------------------
const unavailability = z.strictObject({
    windowId: uuid,
    reasonCode: z.enum(staffAvailabilityReasonValues),
    startsAt: instant,
    endsAt: instant,
});
/** The query of `GET /staff/team` and `GET /staff/coverage/gaps`. The first page has no cursor. */
export const staffPageQuerySchema = z.strictObject({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: pageCursor.optional(),
});
/** `GET /staff/team` */
export const staffTeamSchema = z.strictObject({
    generatedAt: instant,
    viewer: z.strictObject({
        staffUserId: uuid,
        canManageRoles: z.boolean(),
        canManageStatus: z.boolean(),
        canManageCoverage: z.boolean(),
    }),
    members: z
        .array(z.strictObject({
        staffUserId: uuid,
        displayName,
        status: z.enum(staffTeamStatusValues),
        roles: z.array(z.enum(staffTeamRoleValues)).max(4),
        activeAssignmentCount: z.number().int().min(0).max(100_000),
        unavailability: z.array(unavailability).max(50),
        version: versionToken,
    }))
        .max(100),
    /** The access review counts over the WHOLE team: a page never changes them. */
    summary: z.strictObject({
        total: z.number().int().min(0).max(100_000),
        activeByRole: z.strictObject({
            concierge: z.number().int().min(0).max(100_000),
            supervisor: z.number().int().min(0).max(100_000),
            catalog_editor: z.number().int().min(0).max(100_000),
            administrator: z.number().int().min(0).max(100_000),
        }),
        activeWithoutRole: z.strictObject({
            count: z.number().int().min(0).max(100_000),
            names: z.array(displayName).max(10),
        }),
    }),
    /** Pass it back as `cursor` for the next page. `null` on the last page. */
    nextCursor: pageCursor.nullable(),
});
/** `GET /staff/customers/:customerId/coverage` */
export const staffCustomerCoverageSchema = z.strictObject({
    generatedAt: instant,
    customer: customerRef,
    assignments: z
        .array(z.strictObject({
        assignmentId: uuid,
        type: z.enum(staffAssignmentTypeValues),
        staff: staffRef,
        startsAt: instant,
        endsAt: instant.nullable(),
        effective: z.boolean(),
        /** A TEMPORARY window that has passed but whose row is still ACTIVE: no cover now, and a new window may be added (review fix 4). */
        elapsed: z.boolean(),
    }))
        .max(20),
    hasEffectivePrimary: z.boolean(),
    unavailability: z.array(unavailability.extend({ staff: staffRef })).max(50),
    conversation: z.strictObject({ id: uuid, mode: z.string().min(1).max(40) }).nullable(),
    candidates: z.array(staffRef).max(200),
});
/** `GET /staff/coverage/gaps` */
export const staffCoverageGapsSchema = z.strictObject({
    gaps: z
        .array(z.strictObject({
        kind: z.enum(staffCoverageGapKindValues),
        customer: customerRef,
        conversationId: uuid.nullable(),
        handoffId: uuid.nullable(),
        since: instant,
    }))
        .max(100),
    /** Pass it back as `cursor` for the next page. `null` on the last page. */
    nextCursor: pageCursor.nullable(),
});
export const staffSupportActivityKindValues = ["request", "payment", "handoff", "trip"];
/** `GET /staff/customers/:customerId/support` */
export const staffCustomerSupportSchema = z.strictObject({
    generatedAt: instant,
    customer: customerRef,
    viewer: z.strictObject({
        isSupervisor: z.boolean(),
        mayManageCoverage: z.boolean(),
        mayReadMemory: z.boolean(),
    }),
    coverage: z.strictObject({
        hasEffectivePrimary: z.boolean(),
        assignments: z
            .array(z.strictObject({
            assignmentId: uuid,
            type: z.enum(staffAssignmentTypeValues),
            staff: staffRef,
            endsAt: instant.nullable(),
        }))
            .max(20),
    }),
    conversation: z
        .strictObject({
        id: uuid,
        mode: z.string().min(1).max(40),
        handoff: z
            .strictObject({
            id: uuid,
            status: z.string().min(1).max(40),
            reasonCode: z.string().min(1).max(64),
        })
            .nullable(),
    })
        .nullable(),
    requests: z
        .strictObject({
        open: z
            .array(z.strictObject({
            id: uuid,
            requestType: z.string().min(1).max(64),
            state: z.string().min(1).max(40),
            title: z.string().max(200),
            updatedAt: instant,
        }))
            .max(10),
    })
        .nullable(),
    payments: z
        .strictObject({
        open: z
            .array(z.strictObject({
            id: uuid,
            status: z.string().min(1).max(40),
            amountMinor: z.number().int(),
            currency: z.string().min(1).max(8),
            statusChangedAt: instant,
        }))
            .max(10),
    })
        .nullable(),
    trips: z
        .array(z.strictObject({
        id: uuid,
        status: z.string().min(1).max(40),
        title: z.string().max(200),
        startsOn: z.string().max(10).nullable(),
        endsOn: z.string().max(10).nullable(),
    }))
        .max(5)
        .nullable(),
    recentActivity: z
        .array(z.strictObject({
        kind: z.enum(staffSupportActivityKindValues),
        id: uuid,
        code: z.string().min(1).max(40),
        at: instant,
    }))
        .max(10),
});
// --- command bodies --------------------------------------------------------------------------------------------------------------
const expectedAssignments = z.array(uuid).max(20);
/** `POST /staff/team/:staffUserId/roles/grant` and `/roles/revoke` */
export const staffRoleBodySchema = z.strictObject({
    role: z.enum(staffTeamRoleValues),
    reason: staffReason,
    expectedVersion: versionToken,
});
/** `POST /staff/team/:staffUserId/offboard` and `/reactivate` */
export const staffStatusBodySchema = z.strictObject({
    reason: staffReason,
    expectedVersion: versionToken,
});
/** `POST /staff/customers/:customerId/coverage/primary` and `/backup` */
export const staffAssignBodySchema = z.strictObject({
    staffUserId: uuid,
    reason: staffReason,
    expectedActiveAssignmentIds: expectedAssignments,
});
/** `POST /staff/customers/:customerId/coverage/primary/reassign` */
export const staffReassignPrimaryBodySchema = staffAssignBodySchema.extend({
    previousPrimaryBecomes: z.enum(["ENDED", "BACKUP"]),
});
/** `POST /staff/customers/:customerId/coverage/temporary` */
export const staffTemporaryCoverageBodySchema = staffAssignBodySchema.extend({
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
});
/** `POST /staff/customers/:customerId/coverage/:assignmentId/end` */
export const staffEndAssignmentBodySchema = z.strictObject({
    reason: staffReason,
    expectedActiveAssignmentIds: expectedAssignments,
});
/** `POST /staff/team/:staffUserId/unavailability` */
export const staffUnavailabilityBodySchema = z.strictObject({
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    reasonCode: z.enum(staffAvailabilityReasonValues),
    reason: staffReason,
    /** The ids of the open or future windows the screen showed for this person. A window made or ended since is a 409. */
    expectedOpenWindowIds: z.array(uuid).max(50),
});
/** `POST /staff/unavailability/:windowId/end` */
export const staffEndUnavailabilityBodySchema = z.strictObject({ reason: staffReason });
// --- the refusal of a command ------------------------------------------------------------------------------------------------
/**
 * The rule codes a team or coverage command can be refused with (409). `staff.stale_view` and `assignment.stale_view` mean the screen
 * showed old data: re-read and decide again. The others name the state that blocks the command. A code, never text.
 */
export const staffTeamConflictCodeValues = [
    "staff.stale_view",
    "assignment.stale_view",
    "availability.stale_view",
    "staff.role_already_granted",
    "staff.role_not_granted",
    "staff.already_deactivated",
    "staff.not_deactivated",
    "staff.last_administrator",
    "assignment.primary_already_active",
    "assignment.no_active_primary",
    "assignment.same_concierge",
    "assignment.concierge_already_assigned",
    "assignment.backup_already_active",
    "assignment.temporary_already_active",
    "assignment.not_active",
    "availability.window_not_open",
];
/** The extra key `teamConflict` of a refused team or coverage command. */
export const staffTeamConflictSchema = z.strictObject({
    code: z.enum(staffTeamConflictCodeValues),
});
